/**
 * Authentication Error Types and Constants
 * 
 * Centralized error handling for Better Auth API responses.
 * Maps API error codes to user-friendly error types for UI handling.
 */

/**
 * Authentication error types for UI handling.
 * Maps to user-facing error messages via i18n.
 * 
 * Using const object instead of enum due to erasableSyntaxOnly TypeScript config.
 */
export const AuthErrorType = {
  /** Invalid email or password */
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  /** User account is banned */
  USER_BANNED: 'USER_BANNED',
  /** User account not found */
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  /** User is not authorized (missing admin role) */
  UNAUTHORIZED: 'UNAUTHORIZED',
  /** Too many login attempts */
  RATE_LIMITED: 'RATE_LIMITED',
  /** Network or connection error */
  NETWORK_ERROR: 'NETWORK_ERROR',
  /** Session has expired */
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  /** Invalid session token */
  INVALID_SESSION: 'INVALID_SESSION',
  /** OAuth provider error */
  OAUTH_ERROR: 'OAUTH_ERROR',
  /** OAuth was cancelled by user */
  OAUTH_CANCELLED: 'OAUTH_CANCELLED',
  /** Email not verified (if required) */
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  /** Server unavailable */
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  /** Unknown or unexpected error */
  UNKNOWN: 'UNKNOWN',
} as const;

/** Type for AuthErrorType values */
export type AuthErrorType = (typeof AuthErrorType)[keyof typeof AuthErrorType];

/**
 * Better Auth API error codes.
 * These are returned by the backend in the error response.
 */
export const BETTER_AUTH_ERROR_CODES = {
  /** Invalid credentials provided */
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  /** User is banned from the system */
  USER_BANNED: 'USER_BANNED',
  /** User not found */
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  /** Unauthorized access */
  UNAUTHORIZED: 'UNAUTHORIZED',
  /** Rate limit exceeded */
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  /** Invalid session */
  INVALID_SESSION: 'INVALID_SESSION',
  /** Session expired */
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  /** OAuth error */
  OAUTH_ERROR: 'OAUTH_ERROR',
  /** Internal server error */
  INTERNAL_ERROR: 'INTERNAL_ERROR',
} as const;

/**
 * Custom error class for authentication errors.
 * Provides typed error handling with additional context.
 */
export class AuthError extends Error {
  public readonly type: AuthErrorType;
  public readonly code: string;
  public readonly status: number;
  public readonly details?: Record<string, unknown>;

  constructor(
    type: AuthErrorType,
    message: string,
    code: string,
    status: number,
    details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'AuthError';
    this.type = type;
    this.code = code;
    this.status = status;
    this.details = details;
  }

  /**
   * Create an AuthError from an API error response.
   */
  static fromApiError(error: {
    code?: string;
    message?: string;
    status?: number;
    details?: Record<string, unknown>;
  }): AuthError {
    const type = mapApiErrorToType(error.code);
    return new AuthError(
      type,
      error.message || 'An unexpected error occurred',
      error.code || 'UNKNOWN',
      error.status || 500,
      error.details
    );
  }

  /**
   * Create a network error.
   */
  static networkError(originalError?: Error): AuthError {
    return new AuthError(
      AuthErrorType.NETWORK_ERROR,
      'Network error. Please check your connection.',
      'NETWORK_ERROR',
      0,
      { originalError: originalError?.message }
    );
  }

  /**
   * Create a service unavailable error.
   */
  static serviceUnavailable(): AuthError {
    return new AuthError(
      AuthErrorType.SERVICE_UNAVAILABLE,
      'Service temporarily unavailable. Please try again later.',
      'SERVICE_UNAVAILABLE',
      503
    );
  }
}

/**
 * Map API error code to AuthErrorType for UI handling.
 */
export function mapApiErrorToType(code?: string): AuthErrorType {
  if (!code) return AuthErrorType.UNKNOWN;

  const codeUpper = code.toUpperCase();

  switch (codeUpper) {
    case BETTER_AUTH_ERROR_CODES.INVALID_CREDENTIALS:
      return AuthErrorType.INVALID_CREDENTIALS;
    case BETTER_AUTH_ERROR_CODES.USER_BANNED:
      return AuthErrorType.USER_BANNED;
    case BETTER_AUTH_ERROR_CODES.USER_NOT_FOUND:
      return AuthErrorType.USER_NOT_FOUND;
    case BETTER_AUTH_ERROR_CODES.UNAUTHORIZED:
      return AuthErrorType.UNAUTHORIZED;
    case BETTER_AUTH_ERROR_CODES.RATE_LIMIT_EXCEEDED:
      return AuthErrorType.RATE_LIMITED;
    case BETTER_AUTH_ERROR_CODES.INVALID_SESSION:
      return AuthErrorType.INVALID_SESSION;
    case BETTER_AUTH_ERROR_CODES.SESSION_EXPIRED:
      return AuthErrorType.SESSION_EXPIRED;
    case BETTER_AUTH_ERROR_CODES.OAUTH_ERROR:
      return AuthErrorType.OAUTH_ERROR;
    default:
      return AuthErrorType.UNKNOWN;
  }
}

/**
 * Get the i18n translation key for an error type.
 * Use with: t(`auth.errors.${getErrorTranslationKey(errorType)}`)
 */
export function getErrorTranslationKey(type: AuthErrorType): string {
  switch (type) {
    case AuthErrorType.INVALID_CREDENTIALS:
      return 'invalidCredentials';
    case AuthErrorType.USER_BANNED:
      return 'userBanned';
    case AuthErrorType.USER_NOT_FOUND:
      return 'userNotFound';
    case AuthErrorType.UNAUTHORIZED:
      return 'unauthorized';
    case AuthErrorType.RATE_LIMITED:
      return 'tooManyRequests';
    case AuthErrorType.NETWORK_ERROR:
      return 'networkError';
    case AuthErrorType.SESSION_EXPIRED:
      return 'sessionExpired';
    case AuthErrorType.INVALID_SESSION:
      return 'sessionExpired';
    case AuthErrorType.OAUTH_ERROR:
      return 'oauthError';
    case AuthErrorType.OAUTH_CANCELLED:
      return 'oauthCancelled';
    case AuthErrorType.EMAIL_NOT_VERIFIED:
      return 'emailNotVerified';
    case AuthErrorType.SERVICE_UNAVAILABLE:
      return 'serviceUnavailable';
    case AuthErrorType.UNKNOWN:
    default:
      return 'unknown';
  }
}

/**
 * Check if an error is a specific AuthErrorType.
 */
export function isAuthError(error: unknown): error is AuthError {
  return error instanceof AuthError;
}

/**
 * Check if an error indicates the user should be logged out.
 * Used to trigger automatic logout on session issues.
 */
export function shouldLogoutOnError(type: AuthErrorType): boolean {
  const logoutErrors: AuthErrorType[] = [
    AuthErrorType.SESSION_EXPIRED,
    AuthErrorType.INVALID_SESSION,
    AuthErrorType.USER_BANNED,
    AuthErrorType.UNAUTHORIZED,
  ];
  return logoutErrors.includes(type);
}
